import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { toast } from 'sonner';

// Source of truth: shop_assets.category CHECK constraint.
// Initial 9 values added by 20260114025212_*.sql; Batch 3 widened to 19
// via 20260607000000_optiassets_batch3_widen_categories_and_asset_type.sql.
// Keep this union, ASSET_CATEGORY_VALUES, and ASSET_CATEGORY_LABELS in
// lockstep with the migration; tests/unit/optiassets/optiAssetsBatch1Consistency
// pins the 3-way parity (DB CHECK ⇔ TS const ⇔ labels).
export const ASSET_CATEGORY_VALUES = [
  // Existing 9 — bay-shapes (auto-created by ensure_bay_has_asset trigger)
  // plus the original portable equipment set.
  'lift_bay',
  'flat_bay',
  'alignment',
  'ac_machine',
  'scan_tool',
  'tire_machine',
  'compressor',
  'adas',
  'other',
  // Batch 3 additions (10) — broad equipment groupings. Specificity
  // beyond these values lives in the free-text `asset_type` column.
  'diagnostic_equipment',
  'computer_equipment',
  'shop_equipment',
  'specialty_tooling',
  'wheel_balancer',
  'battery_equipment',
  'fluid_service_machine',
  'welder',
  'press',
  'software_subscription',
] as const;

export type AssetCategory = typeof ASSET_CATEGORY_VALUES[number];

export type AssetStatus = 
  | 'active' | 'out_of_service' | 'maintenance' | 'awaiting_parts' | 'retired';

export interface ShopAsset {
  id: string;
  shop_id: string;
  name: string;
  category: AssetCategory;
  // Batch 3 — free-text subtype (e.g. "PicoScope 4425A", "Service advisor laptop").
  // Complements the controlled `category`. NULL on rows created before Batch 3.
  asset_type: string | null;
  manufacturer: string | null;
  model: string | null;
  serial: string | null;
  home_bay_id: string | null;
  is_portable: boolean;
  home_location_text: string | null;
  status: AssetStatus;
  status_changed_at: string | null;
  status_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  // Joined data
  home_bay?: { id: string; name: string } | null;
}

export interface CreateAssetInput {
  name: string;
  category: AssetCategory;
  asset_type?: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  home_bay_id?: string;
  is_portable?: boolean;
  home_location_text?: string;
  notes?: string;
}

export interface UpdateAssetInput extends Partial<CreateAssetInput> {
  status?: AssetStatus;
  status_reason?: string;
}

export function useAssets() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['shop-assets', shopId],
    queryFn: async () => {
      if (!shopId) return [];
      
      const { data, error } = await supabase
        .from('shop_assets')
        .select(`
          *,
          home_bay:shop_bays(id, name)
        `)
        .eq('shop_id', shopId)
        .order('name');
      
      if (error) throw error;
      return data as ShopAsset[];
    },
    enabled: !!shopId,
  });
}

export function useAsset(assetId: string | null) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  return useQuery({
    queryKey: ['shop-asset', assetId],
    queryFn: async () => {
      if (!assetId || !shopId) return null;
      
      const { data, error } = await supabase
        .from('shop_assets')
        .select(`
          *,
          home_bay:shop_bays(id, name)
        `)
        .eq('id', assetId)
        .eq('shop_id', shopId)
        .single();
      
      if (error) throw error;
      return data as ShopAsset;
    },
    enabled: !!assetId && !!shopId,
  });
}

/**
 * Lightweight duplicate check: does this shop already have an asset
 * with the given serial number? Returns the matching {id, name} pair
 * if found, or null. Used by AssetCreateDialog to surface a soft
 * warning before create — never hard-blocks (the DB does not enforce
 * uniqueness on serial, so this is purely advisory UX). Trimmed
 * empty strings disable the query.
 */
export function useAssetSerialDuplicate(serial: string) {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;
  const trimmed = serial.trim();

  return useQuery({
    queryKey: ['shop-asset-serial-duplicate', shopId, trimmed],
    queryFn: async () => {
      if (!shopId || !trimmed) return null;
      const { data, error } = await supabase
        .from('shop_assets')
        .select('id, name')
        .eq('shop_id', shopId)
        .eq('serial', trimmed)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as { id: string; name: string } | null;
    },
    enabled: !!shopId && trimmed.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useCreateAsset() {
  const { activeShop, user } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (input: CreateAssetInput) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to create assets');

      const { data, error } = await supabase
        .from('shop_assets')
        .insert({
          shop_id: shopId,
          created_by: user?.id,
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-assets', shopId] });
      toast.success('Asset created');
    },
    onError: (error) => {
      toast.error('Failed to create asset', { description: error.message });
    },
  });
}

export function useUpdateAsset() {
  const { activeShop } = useAuth();
  const { canEditAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateAssetInput & { id: string }) => {
      if (!shopId) throw new Error('No active shop');
      if (!canEditAssets) throw new Error('You do not have permission to edit assets');

      const updateData: Record<string, unknown> = { ...input };

      // Track status changes
      if (input.status) {
        updateData.status_changed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('shop_assets')
        .update(updateData)
        .eq('id', id)
        .eq('shop_id', shopId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['shop-assets', shopId] });
      queryClient.invalidateQueries({ queryKey: ['shop-asset', data.id] });
      toast.success('Asset updated');
    },
    onError: (error) => {
      toast.error('Failed to update asset', { description: error.message });
    },
  });
}

export function useDeleteAsset() {
  const { activeShop } = useAuth();
  const { canDeleteAssets } = usePermissions();
  const queryClient = useQueryClient();
  const shopId = activeShop?.id;

  return useMutation({
    mutationFn: async (assetId: string) => {
      if (!shopId) throw new Error('No active shop');
      if (!canDeleteAssets) throw new Error('You do not have permission to delete assets');

      const { error } = await supabase
        .from('shop_assets')
        .delete()
        .eq('id', assetId)
        .eq('shop_id', shopId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop-assets', shopId] });
      toast.success('Asset deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete asset', { description: error.message });
    },
  });
}

// Asset category labels — keys MUST match AssetCategory union (the DB
// CHECK constraint). Future-additive categories require a migration
// widening the CHECK + updating ASSET_CATEGORY_VALUES above; the test
// at tests/unit/optiassets/optiAssetsBatch1Consistency pins parity.
export const ASSET_CATEGORY_LABELS: Record<AssetCategory, string> = {
  lift_bay: 'Lift Bay',
  flat_bay: 'Flat Bay',
  alignment: 'Alignment Rack',
  ac_machine: 'A/C Machine',
  scan_tool: 'Scan Tool',
  tire_machine: 'Tire Machine',
  compressor: 'Compressor',
  adas: 'ADAS Equipment',
  other: 'Other',
  // Batch 3 additions
  diagnostic_equipment: 'Diagnostic Equipment',
  computer_equipment: 'Computer / Tablet',
  shop_equipment: 'Shop Equipment',
  specialty_tooling: 'Specialty Tooling',
  wheel_balancer: 'Wheel Balancer',
  battery_equipment: 'Battery Equipment',
  fluid_service_machine: 'Fluid Service Machine',
  welder: 'Welder',
  press: 'Press',
  software_subscription: 'Software / Subscription',
};

// Asset status labels
export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  active: 'Active',
  out_of_service: 'Out of Service',
  maintenance: 'In Maintenance',
  awaiting_parts: 'Awaiting Parts',
  retired: 'Retired',
};

// Asset status colors
export const ASSET_STATUS_COLORS: Record<AssetStatus, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  out_of_service: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  maintenance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  awaiting_parts: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  retired: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};
